import React, { useState } from 'react';
import './TagsPage.scss';
import Title from '../../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import Loader from '../../../shared/components/common/loader/Loader';
import { Navigate, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import ActionBtn from './components/action-btn/ActionBtn';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { PROJECTS } from '../../../shared/routes/path';
import { useGetTags } from '../../../shared/api/hooks/tags/useGetTags';
import { useUpdateTags } from '../../../shared/api/hooks/tags/useUpdateTags';
import { useRemoveTags } from '../../../shared/api/hooks/tags/useRemoveTags';
import { TagsResponse } from '../../../shared/models/tags/TagsResponse';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { keywordsTemplate } from '../../../shared/helpers/ColumnHelper';
import { format } from 'date-fns';
import EditTagDialog from './components/edit-tag-dialog/EditTagDialog';
import EmptyState from '../../../shared/components/common/empty-state/EmptyState';
import { emptyStateData } from './helpers/TagsHelper';
import { useGetCommonKeywords } from '../../../shared/api/hooks/monitor/keyword/useGetCommonKeywords';
import { Dialog } from 'primereact/dialog';
import { getCurrentProject } from '../../../shared/helpers/project/ProjectHelper';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';

const TagsPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId);

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    isCancelled: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<TagsResponse | null>(null);

  const { tags, isLoading } = useGetTags(projectIdNumber, { raw: true });
  const { mutate: removeTag } = useRemoveTags(projectIdNumber);
  const { mutate: updateTag } = useUpdateTags(projectIdNumber);
  const { commonKeywords, isLoading: isCommonKeywordsLoading } = useGetCommonKeywords(projectIdNumber);
  const { accountResponse, projectResponse } = useAccount();

  const openEditDialog = (projectId: number | null, tag: TagsResponse | null) => {
    if (!projectId) return;
    setEditingTag(tag);
    setDialogVisible(true);
  };

  const handleSubmit = (data: Partial<TagsResponse>) => {
    updateTag(data);
    setDialogVisible(false);
  };

  if (
    !accountResponse ||
    !accountResponse.addons.includes('TAGS') ||
    !!getCurrentProject(projectResponse || [], projectIdNumber)?.demoVersion
  ) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (isLoading || isCommonKeywordsLoading) {
    return <Loader />;
  }

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      global: { ...prev.global, value },
    }));
    setGlobalFilterValue(value);
  };

  const getAddTagButton = () => {
    if (['BLOCKED'].includes(accountResponse.accountStatus)) return <></>;
    return (
      <Button
        className="btn btn--sm"
        onClick={() => openEditDialog(projectIdNumber, null)}
        label={t('addNewTag')}
        id="add-prompt"
      />
    );
  };

  const buttonAndSearch = () => (
    <>
      <div className="page-box__buttons">
        <div className="input-search-box">
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search..."
            className="input-search"
          />
        </div>
      </div>
      {getAddTagButton()}
    </>
  );

  const tagsIsEmpty = !isLoading && tags && tags.length === 0;

  return (
    <>
      <div className="tags-page page-content">
        <div className="page-box">
          <Title
            title={t('tags')}
            children={tagsIsEmpty ? undefined : buttonAndSearch()}
            childrenOnMain={
              tagsIsEmpty ? undefined : (
                <div className="title-box__subtitle">{t('tagInfo')?.replaceAll('</br>', ' ')}</div>
              )
            }
          />

          {tagsIsEmpty ? (
            <EmptyState emptyStateData={emptyStateData(getAddTagButton())} />
          ) : (
            <div className="page-box__stats">
              {tags && tags.length > 0 && (
                <div className="table-content">
                  <DataTable
                    stripedRows
                    removableSort
                    value={tags}
                    filters={filters}
                    tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                    tableClassName="table"
                    sortField={'searchVolume'}
                    sortOrder={-1}
                    className="table-wrapper"
                  >
                    <Column
                      sortable
                      field="label"
                      header={t('name')}
                      headerClassName="table__header"
                      style={{ minWidth: '120px', maxWidth: '100%' }}
                    />

                    <Column
                      field={'keywordsData'}
                      header={t('keywords')}
                      headerClassName="table__header"
                      body={(row: TagsResponse) => {
                        const uniqueKeywords = Object.values(
                          row.keywordsData.reduce<
                            Record<string, { keyword: string; normalizedKeyword: string }>
                          >((acc, k) => {
                            if (!acc[k.normalizedKeyword]) {
                              acc[k.normalizedKeyword] = k;
                            }
                            return acc;
                          }, {})
                        );
                        return keywordsTemplate(uniqueKeywords.map((k) => k.keyword));
                      }}
                      style={{ width: '320px' }}
                    />

                    <Column
                      sortable
                      field={'created'}
                      header={t('created')}
                      headerClassName="table__header"
                      body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
                      style={{ width: '160px' }}
                    />

                    <Column
                      frozen
                      alignFrozen="right"
                      header={t('actions')}
                      body={(tag) => (
                        <MoreBtn
                          children={
                            <ActionBtn
                              projectId={projectIdNumber}
                              accountStatus={accountResponse.accountStatus}
                              onEdit={() => openEditDialog(projectIdNumber, tag)}
                              removeTag={removeTag}
                              tag={tag}
                            />
                          }
                          rowId={tag.id || 0}
                        />
                      )}
                      bodyClassName="table__cell--gap table__cell--center"
                      headerClassName="table__header table__header--center"
                      align="center"
                      style={{ width: '34px' }}
                    />
                  </DataTable>
                </div>
              )}
            </div>
          )}
        </div>

        <Dialog
          visible={dialogVisible}
          header={t(editingTag ? 'editTag' : 'createNewTag')}
          onHide={() => setDialogVisible(false)}
          className="dialog-box dialog-box--no-footer"
          draggable={false}
          resizable={false}
        >
          <EditTagDialog
            currentTags={tags}
            projectId={projectIdNumber}
            tag={editingTag}
            onSubmit={handleSubmit}
            keywordsOptions={commonKeywords}
          />
        </Dialog>
      </div>
    </>
  );
};

export default TagsPage;
